import customAxios from "lib/axios";
import { getAppRuntimeMetadata } from "lib/appDeviceTracking";

const VAPID_PUBLIC_KEY = process.env.REACT_APP_WEB_PUSH_PUBLIC_KEY || "";
const PUSH_SUBSCRIPTIONS_PATH =
  process.env.REACT_APP_WEB_PUSH_SUBSCRIPTIONS_PATH ||
  "/notifications/push/subscriptions";
const SERVICE_WORKER_READY_TIMEOUT_MS = 10000;

type PushPermissionResult = {
  ok: boolean;
  permission: NotificationPermission | "unsupported" | "missing-config";
  reason?:
    | "denied"
    | "dismissed"
    | "unsupported"
    | "missing-config"
    | "save-failed";
};

const isBrowserPushCapable = () => {
  if (typeof window === "undefined") return false;

  return (
    "Notification" in window &&
    "serviceWorker" in navigator &&
    "PushManager" in window
  );
};

export const isPushNotificationsSupported = () => {
  return isBrowserPushCapable() && Boolean(VAPID_PUBLIC_KEY);
};

const urlBase64ToUint8Array = (base64String: string) => {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; i += 1) {
    outputArray[i] = rawData.charCodeAt(i);
  }

  return outputArray;
};

const withTimeout = <T>(promise: Promise<T>, timeoutMs: number): Promise<T> => {
  return new Promise((resolve, reject) => {
    const timeout = window.setTimeout(() => {
      reject(new Error("Service worker registration timed out"));
    }, timeoutMs);

    promise.then(
      (value) => {
        window.clearTimeout(timeout);
        resolve(value);
      },
      (error) => {
        window.clearTimeout(timeout);
        reject(error);
      }
    );
  });
};

const getPushServiceWorkerRegistration = async () => {
  const publicPath = process.env.PUBLIC_URL || "";
  const scope = `${publicPath}/`.replace(/\/+/g, "/");

  if (process.env.NODE_ENV !== "production") {
    await withTimeout(
      navigator.serviceWorker.register(
        `${publicPath}/push-service-worker.js`,
        { scope }
      ),
      SERVICE_WORKER_READY_TIMEOUT_MS
    );
    return withTimeout(
      navigator.serviceWorker.ready,
      SERVICE_WORKER_READY_TIMEOUT_MS
    );
  }

  const existing = await navigator.serviceWorker.getRegistration(scope);
  if (!existing) {
    await navigator.serviceWorker.register(`${publicPath}/service-worker.js`, {
      scope,
    });
  }

  return withTimeout(
    navigator.serviceWorker.ready,
    SERVICE_WORKER_READY_TIMEOUT_MS
  );
};

const persistPushSubscription = async (subscription: PushSubscription) => {
  const runtime = getAppRuntimeMetadata();

  await customAxios.post(
    PUSH_SUBSCRIPTIONS_PATH,
    {
      subscription: subscription.toJSON(),
      endpoint: subscription.endpoint,
      keys: {
        p256dh: subscription.toJSON().keys?.p256dh || "",
        auth: subscription.toJSON().keys?.auth || "",
      },
      ...runtime,
    },
    { timeout: SERVICE_WORKER_READY_TIMEOUT_MS }
  );
};

export const syncExistingPushSubscriptionSilently = async () => {
  if (!isPushNotificationsSupported()) return;
  if (Notification.permission !== "granted") return;

  const registration = await getPushServiceWorkerRegistration();
  const subscription = await withTimeout(
    registration.pushManager.getSubscription(),
    SERVICE_WORKER_READY_TIMEOUT_MS
  );

  if (!subscription) return;

  try {
    await persistPushSubscription(subscription);
  } catch (error) {
    console.error("[push] Failed to sync subscription", error);
  }
};

export const subscribeToPushNotifications =
  async (): Promise<PushPermissionResult> => {
    if (!isBrowserPushCapable()) {
      return {
        ok: false,
        permission: "unsupported",
        reason: "unsupported",
      };
    }

    if (!VAPID_PUBLIC_KEY) {
      return {
        ok: false,
        permission: "missing-config",
        reason: "missing-config",
      };
    }

    let permission = Notification.permission;
    if (permission === "default") {
      permission = await Notification.requestPermission();
    }

    if (permission !== "granted") {
      return {
        ok: false,
        permission,
        reason: permission === "denied" ? "denied" : "dismissed",
      };
    }

    const registration = await getPushServiceWorkerRegistration();
    const existingSubscription = await withTimeout(
      registration.pushManager.getSubscription(),
      SERVICE_WORKER_READY_TIMEOUT_MS
    );

    let subscription = existingSubscription;
    let createdNow = false;

    if (!subscription) {
      subscription = await withTimeout(
        registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
        }),
        SERVICE_WORKER_READY_TIMEOUT_MS
      );
      createdNow = true;
    }

    try {
      await persistPushSubscription(subscription);
      return { ok: true, permission };
    } catch (error) {
      if (createdNow) {
        try {
          await subscription.unsubscribe();
        } catch (unsubscribeError) {
          console.error(
            "[push] Failed to rollback subscription after save error",
            unsubscribeError
          );
        }
      }

      console.error("[push] Failed to persist subscription", error);
      return { ok: false, permission, reason: "save-failed" };
    }
  };
